import Vue from 'vue';
import { stripMarkup } from '@/utils/string';

const loadScript = (tealiumURL, payload, callbackLoadCompleted) => {
  const data = document.createElement('script');
  data.setAttribute('type', 'text/javascript');
  data.innerHTML = 'var utag_data = ' + JSON.stringify(payload) + ';';
  document.body.appendChild(data);
  const script = document.createElement('script');
  script.setAttribute('type', 'text/javascript');
  script.setAttribute('src', tealiumURL);
  script.async = true;
  document.body.appendChild(script);

  let loaded = false;
  script.onload = script.onreadystatechange = function () {
    if (!loaded && (!this.readyState || this.readyState === 'complete')) {
      loaded = true;
      callbackLoadCompleted();
    }
  };
};

const umlautTranslator = (s) => {
  return s
    .replace(/[ÂÀÅÃ]/g, 'A')
    .replace(/[âàåã]/g, 'a')
    .replace(/Ä/g, 'AE')
    .replace(/ä/g, 'ae')
    .replace(/Ç/g, 'C')
    .replace(/ç/g, 'c')
    .replace(/[ÉÊÈË]/g, 'E')
    .replace(/[éêèë]/g, 'e')
    .replace(/[ÓÔÒÕØ]/g, 'O')
    .replace(/[óôòõ]/g, 'o')
    .replace(/Ö/g, 'OE')
    .replace(/ö/g, 'oe')
    .replace(/Š/g, 'S')
    .replace(/š/g, 's')
    .replace(/ß/g, 'ss')
    .replace(/[ÚÛÙ]/g, 'U')
    .replace(/[úûù]/g, 'u')
    .replace(/Ü/g, 'UE')
    .replace(/ü/g, 'ue')
    .replace(/[ÝŸ]/g, 'Y')
    .replace(/[ýÿ]/g, 'y')
    .replace(/Ž/g, 'Z')
    .replace(/ž/, 'z');
};

const simplify = (text) => {
  return umlautTranslator(stripMarkup(text))
    .toLowerCase()
    .replace(/[^a-z0-9 _-]+/g, '');
};

/**
 * strip html tags and encodings (e.g. &amp; -> &), simplify umlauts (e.g. ä -> ae), convert to lower-case, and keep only alphanumeric characters and spaces
 * (where other characters will be removed except for dash (-) and underscore (_) which turn into space)
 */
const simplifyWithSpace = (text) => {
  return simplify(text)
    .replace(/[ _-]+/g, ' ')
    .trim();
};

/**
 * strip html tags and encodings (e.g. &amp; -> &), simplify umlauts (e.g. ä -> ae), convert to lower-case, and keep only alphanumeric characters and underscores
 * (where other characters will be removed except for space and dash (-) which turn into underscore (_))
 */
const simplifyWithUnderscore = (text) => {
  return simplify(text)
    .replace(/[ _-]+/g, '_')
    .trim();
};

export const view = async (ctx, store, tealiumURL, pageContentSuffix, pageTypeOrPayload) => {
  let promiseCallbackResolve;
  let isFirstCall = false;
  if (ctx.webtrekkPromise === undefined) {
    isFirstCall = true;
    ctx.webtrekkPromise = new Promise((resolve) => {
      promiseCallbackResolve = resolve;
    });
  }

  // allow page to render first (so there is no delay by webtrekk)
  Vue.nextTick(async () => {
    const payload = {};

    if (typeof pageTypeOrPayload === 'string') {
      payload.page_type = pageTypeOrPayload;
    } else {
      Object.assign(payload, pageTypeOrPayload);
      if (payload.shop_product_name) {
        payload.shop_product_name = simplifyWithSpace(payload.shop_product_name);
      }
    }

    if (payload.page_content_id === undefined) {
      payload.page_content_id = `${store.state.system.webtrekkFixedContentDomain}.alle.${pageContentSuffix}`;
    }

    if (payload.invoking_system === undefined && store.state.system.userProfile != null) {
      payload.invoking_system = store.state.system.userProfile;
    }

    if (store.state.system.pageContext === 'CITA') {
      payload.shop_product_sku = `${store.state.cita.xtcContractId}_${store.state.cita.partnerId}`;
    }

    if (isFirstCall) {
      loadScript(tealiumURL, payload, promiseCallbackResolve);
    } else {
      await ctx.webtrekkPromise;
      // eslint-disable-next-line no-undef
      if (typeof utag === 'object' && typeof utag.view === 'function') {
        // eslint-disable-next-line no-undef
        utag.view(payload);
      } else {
        ctx.webtrekkPromise = new Promise((resolve) => {
          promiseCallbackResolve = resolve;
        });
        loadScript(tealiumURL, payload, promiseCallbackResolve);
      }
    }
  });
};

export const link = async (ctx, store, buttonText, target) => {
  if (ctx.webtrekkPromise === undefined) {
    // eslint-disable-net-line no-console
    console.error('Called Webtrekk-link without prior call to Webtrekk-view');
    return;
  }
  await ctx.webtrekkPromise;
  // eslint-disable-next-line no-undef
  if (typeof utag !== 'object' || typeof utag.link !== 'function') {
    // eslint-disable-net-line no-console
    console.error('Lost Webtrekk utag object');
    return;
  }
  let linkId = `content.button.${simplifyWithUnderscore(Vue.prototype.$to(buttonText))}`;
  if (target) {
    if (target.startsWith('https://')) {
      target = target.slice(8);
    } else if (target.startsWith('http://')) {
      target = target.slice(7);
    } else {
      target = `${store.state.system.webtrekkFixedContentDomain}.${target.replace('_', '.')}`;
    }
    linkId += `.${target.toLowerCase().replace('/', '.')}`;
  }
  const trackingDone = new Promise((resolve) => {
    setTimeout(resolve, 500);
    // eslint-disable-next-line no-undef
    utag.link({ wt_link_id: linkId }, resolve);
  });
  await trackingDone;
};

export default { view, link };
